import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { now } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useEvidenceMutation } from "../api/queries/evidence-query";
import { type CaseEvidence, type CaseEvidenceForm, type MarkingStatusForm, markingStatusFormSchema } from "../types/evidence";

export default function CaseEvidenceUpdateMarkingStatusModal({
	data,
}: {
	data: CaseEvidence;
}) {
	const { closeModal } = useModalControl();
	const modal = useModal();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const caseEvidenceMutation = useEvidenceMutation();

	const form = useForm<MarkingStatusForm>({
		resolver: zodResolver(markingStatusFormSchema),
		defaultValues: {
			id: data?.id, //case evidence id
			case_id: data?.case_id,
			evidence_type: data?.evidence_type,
			parties: data?.parties,
			marking_status: data?.marking_status || undefined,
			exhibit: data?.exhibit,
			description: data?.description,
			purpose: data?.purpose,
			updated_marking_status_at: data?.updated_marking_status_at ? format(data?.updated_marking_status_at || now(), "yyyy-MM-dd") : undefined,
		},
	});

	const storedTime = sessionStorage.getItem("password_entered_at");
	const handleSubmit = async (values: CaseEvidenceForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			try {
				setIsLoading(true);
				await caseEvidenceMutation.mutateAsync(values);
				closeModal();
			} catch (error) {
				console.error(error);
				setIsLoading(false);
			}
		} else {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "sm",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							try {
								setIsLoading(true);
								await caseEvidenceMutation.mutateAsync(values);
								closeModal();
							} catch (error) {
								console.error(error);
								setIsLoading(false);
							}
						}}
					/>
				),
			});
		}
	};

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2">
					<div className="col-span-12 w-full">
						<FormField
							control={form.control}
							name="updated_marking_status_at"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Marking Date</FormLabel>
									</div>
									<FormGroup>
										<Input
											autoComplete="off"
											required={
												form.watch("updated_marking_status_at") === undefined ||
												form.watch("updated_marking_status_at") === null ||
												form.watch("updated_marking_status_at")?.startsWith("")
											}
											type="date"
											placeholder="Marking Date"
											{...field}
										/>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="col-span-12 w-full">
						<FormField
							control={form.control}
							name="marking_status"
							render={({ field }) => (
								<FormItem>
									<div className="flex flex-col w-full">
										<FormLabel required>Marking Status</FormLabel>
									</div>
									<FormGroup>
										<Select
											onValueChange={async (value) => {
												field.onChange(value);
											}}
											defaultValue={field.value}
											options={[
												{
													value: "Provisional",
													label: "Provisional",
												},
												{
													value: "Permanent",
													label: "Permanent",
												},
												{
													value: "-",
													label: "-",
												},
											]}
										/>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>
				<div className="flex flex-row justify-between w-full gap-4 mt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" type="submit" variant="default" loading={isLoading}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
